import type { CompleteAttachment } from "@assistant-ui/core";
import {
    AttachmentType,
    type IAttachmentOption,
    type IChatAttachmentBrief,
} from "@/api";
import { getAttachmentFileType } from "./attachment-file-type-registry";

type AuiAttachmentInput = Pick<
    CompleteAttachment,
    "id" | "type" | "name" | "contentType"
>;

/**
 * Narrow assistant-ui's widened `type: string` back to our `AttachmentType`
 * enum. Unknown values fall back to `Document` so downstream rendering never
 * breaks on a type we haven't mapped yet. `Object.values(AttachmentType)` is
 * the single source of truth, so adding a new enum member needs no change here.
 */
export function toAttachmentType(raw: string): AttachmentType {
    return (Object.values(AttachmentType) as string[]).includes(raw)
        ? (raw as AttachmentType)
        : AttachmentType.Document;
}

/**
 * Assistant-ui attachments use two id formats:
 * - Composer-added: `"type:42"` (see `ComposerAttachmentMenu.tsx`).
 * - Backend-loaded: `"42"` (see `convert-messages.ts`).
 * Returns `0` for unparseable input rather than `NaN`, which would poison
 * React keys and URL fragments downstream. An id of `0` is invalid at the
 * backend and produces no navigation side-effects.
 */
export function parseAuiAttachmentId(rawId: string): number {
    const numericPart = rawId.includes(":") ? rawId.split(":")[1] : rawId;
    const parsed = Number(numericPart);
    return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Map an assistant-ui attachment object to the `IChatAttachmentBrief` shape
 * consumed by `AttachmentPillRow`. Input is typed via `Pick<CompleteAttachment>`
 * so a breaking change in `@assistant-ui/core` surfaces here at compile time.
 */
export function mapAuiAttachmentToBrief(
    att: AuiAttachmentInput
): IChatAttachmentBrief {
    const isKb = att.contentType === "kb";
    // `CompleteAttachment` has no slot for file_type, so it rides the
    // side-channel registry keyed by the raw aui id (see
    // attachment-file-type-registry). Absent → omit the key so a PDF/legacy
    // attachment stays PDF-styled downstream.
    const fileType = getAttachmentFileType(att.id);
    return {
        attachment_id: parseAuiAttachmentId(att.id),
        attachment_type: toAttachmentType(att.type),
        attachment_name: isKb ? `KB · ${att.name}` : att.name,
        ...(fileType && { file_type: fileType }),
    };
}

/**
 * Map one of our `IAttachmentOption`s to the assistant-ui `CompleteAttachment`
 * shape. The `"${type}:${id}"` id is the inverse of `parseAuiAttachmentId`, so
 * a composer round-trip recovers the source attachment. `composer.addAttachment`
 * reconstructs the attachment and assigns its own status, so this single value
 * serves both the composer path and the `append()` path without divergence.
 */
export function toAuiAttachment(att: IAttachmentOption): CompleteAttachment {
    return {
        id: `${att.attachment_type}:${att.attachment_id}`,
        type: att.attachment_type,
        name: att.attachment_name,
        contentType: att.is_knowledge_base ? "kb" : undefined,
        content: [],
        status: { type: "complete" },
    };
}
